import { useRef, useState } from 'react';
import { BookingCompressed, Floor, Location } from '@jooxter/api';
import JxtBookingTooltipCard from '../JxtBookingTooltipCard';
import { IJxtSchedulerCalendarTooltip } from './types';
import { useFloating, flip, offset, FloatingPortal, arrow } from '@floating-ui/react';
import './style.scss';

const JxtSchedulerCalendarTooltip = ({ children, eventInfo, isBookingPending }: IJxtSchedulerCalendarTooltip) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const arrowRef = useRef(null);
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom',
    middleware: [
      flip({
        fallbackPlacements: ['bottom-start', 'top', 'top-start', 'left'],
      }),
      offset(4),
      arrow({ element: arrowRef }),
    ],
  });

  const { event } = eventInfo;
  // Never over a booking being dragged or resized (nor its copy following the pointer): it would hide
  // where it lands
  const isMoving = eventInfo.isDragging || eventInfo.isResizing || eventInfo.isMirror;
  const {
    originalBooking: booking = null,
    location,
    floor,
  }: { originalBooking?: BookingCompressed | null; location?: Location; floor?: Floor } = {
    ...event.extendedProps,
  };

  if (!booking) {
    return <></>;
  }

  return (
    <div
      className="z-10 w-full"
      onPointerOver={(e) => {
        // avoid trigger popover on touch devices; nor while a button is held: another booking is being
        // dragged or resized over this one
        if (e.pointerType === 'mouse' && e.buttons === 0) {
          setShowTooltip(true);
        }
      }}
      onPointerOut={() => setShowTooltip(false)}
      // A press starts a drag, a resize or a click: closed at once, not after the drop
      onPointerDown={() => setShowTooltip(false)}
    >
      <div ref={refs.setReference}>{children}</div>
      <FloatingPortal id="planning-scheduler">
        {/* Shown at once, no fade, gone as soon as the booking is left; never in the pointer's way: the
            booking under it can be hovered through it */}
        {!isMoving && showTooltip && (
          <div ref={refs.setFloating} style={floatingStyles} className="pointer-events-none z-[60]">
            <JxtBookingTooltipCard
              booking={booking}
              day={event.start ?? undefined}
              isBookingPending={isBookingPending}
              locationName={location?.name}
              floorNumber={floor?.number}
              nbAttendees={event.extendedProps.nbAttendees}
            />
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};

export default JxtSchedulerCalendarTooltip;
